/**
 * nav_region1 — 홈 지역 필터의 순수 규칙(서버 라우트·클라이언트 섬 공용 · 테스트 대상).
 *
 *   · 홈 서버 HTML(ISR)·크롤러 응답은 언제나 전국이다. 지역은 수화 «뒤» 클라이언트에서만 정해진다(클로킹 0).
 *   · 우선순위: ?r= (URL) > 기억(kd_nav_region_v1) > 전국 — 칩(currentScope)과 같은 규칙(region_home1 규칙 5 · 띠 = 칩).
 *     「전국 보기」 는 기억을 지우고 ?r= 를 뺀다 → 전 섹션 전국.
 */
import { SIDO_NAMES } from '@/lib/regions';
import type { MetaItem } from '@/components/ds2/meta';
import type { HeroStat } from '@/components/ds2/Hero';
import type { HeroCard } from './region-scope-pure';

import type { ThumbData } from '@/lib/thumb/resolve';
export type HomeRegionSource = 'param' | 'memory';
export type HomeRegionPhase = 'mount' | 'change';

const CODE_RE = /^[0-9]{2,5}$/;

/** ?r= · 기억 → 이번에 적용할 지역 코드. phase=mount(첫 판정)에서만 기억을 본다. */
export function resolveHomeRegion(
  param: string | null | undefined,
  memory: string | null | undefined,
  phase: HomeRegionPhase,
): { code: string; source: HomeRegionSource } | null {
  const p = String(param ?? '').trim();
  if (CODE_RE.test(p)) return { code: p, source: 'param' };
  if (p) return null; // 무효 ?r= 는 전국(기억으로 덮지 않는다 — URL 이 이긴다)
  const m = String(memory ?? '').trim();
  // region_home1 규칙 5 — 띠와 칩(GlobalRegionChip · currentScope: ?r= > 기억 > 전국)이 «항상» 같아야 한다.
  //   종전엔 mount 에서만 기억을 봐서, ?r= 화면에서 뒤로가기로 / 에 오면 칩은 기억 지역 · 홈은 전국으로 갈렸다.
  //   「전국 보기」·칩 「전국」 은 기억을 지운 뒤 알리므로(writeRegionMemory(null)) 여기서 전국이 된다.
  void phase;
  if (CODE_RE.test(m)) return { code: m, source: 'memory' };
  return null;
}

/** 시·도 열 값(region_nm·apt_region 등)이 범위 시·도 이름 안인가. 「부산광역시」 ⊃ 「부산」 도 참. */
export function regionMatches(sidoNames: readonly string[], value: string | null | undefined): boolean {
  const v = String(value ?? '').replace(/\s+/g, '');
  if (!v || !sidoNames.length) return false;
  return sidoNames.some((n) => { const k = n.replace(/\s+/g, ''); return !!k && (v === k || v.startsWith(k)); });
}

/** 시군구 값이 시·군 범위 안인가(sigun null = 시·도 전체 = 참). region-filter.inSigun 과 같은 규칙. */
export function sigunMatches(sigunNames: readonly string[] | null, value: string | null | undefined): boolean {
  if (!sigunNames) return true;
  const v = String(value ?? '').replace(/\s+/g, '');
  if (!v) return false;
  return sigunNames.some((n) => { const k = n.replace(/\s+/g, ''); return !!k && (v === k || v.startsWith(k)); });
}

/** 이번 주 청약 후보 → 지역 범위만(시·도 = 일정 region · 시·군 = 현장 sigungu). */
export function filterWeekByScope<T extends { region: string | null; slug: string }>(
  items: readonly T[],
  scope: { sidoNames: readonly string[]; sigunNames: readonly string[] | null },
  sigunguBySlug: Readonly<Record<string, string | null>>,
): T[] {
  return items.filter((r) => regionMatches(scope.sidoNames, r.region) && sigunMatches(scope.sigunNames, sigunguBySlug[r.slug] ?? null));
}

/* ─── 라우트 → 섬 전달 꼴(JSON · 화면 그대로) ─────────────────────────────── */

export interface HomeRegionLink { code: string; label: string }

export interface HomeRegionPayload {
  ok: true;
  code: string;
  tier: 1 | 2;
  /** 「부산」 · 「경남 김해시」. */
  label: string;
  /** 「{지역} 전체 보기」 목적지 — 시·도 허브 /apt/region/{시도} · 시·군이면 그 시·군 허브. */
  hubHref: string;
  /** 「지역별 분양」 타일에서 강조할 시·도 허브 href(서버 타일 href 와 같은 규칙 regionHubHref(top)). */
  sidoHref: string | null;
  /** 0건 섹션의 «인접 지역» 버튼(시·군 → 부모 시·도 · 시·도 → 인접 시·도). */
  neighbors: HomeRegionLink[];
  /** thumb-lists — thumb = 48px 썸네일(lib/thumb · 없으면 종전 카드). */
  schedule: Array<{ key: string; title: string; href: string; date: string; days: number; meta: Array<string | null>; sortKey: string; thumb?: ThumbData | null }>;
  /** post1 3 — 이 지역 분양예정 수(전국 「전체 N곳」 과 같은 정의 · ds2-home fetchUpcomingCountRegion). 모르면 null. */
  upcomingCount?: number | null;
  /** post1 3 — 「전체 N곳 →」 목적지(시·도 = /apt/pipeline?region= · 시·군 = 그 시·군 허브). */
  upcomingHref?: string;
  upcoming: Array<{ key: string; title: string; href: string; thumbSrc: string | null; thumbAlt: string | null; ai: boolean; periodLabel: string | null; periodTone: 'navy' | 'delay' | null; meta: MetaItem[]; sub: string | null; sortKey: string }>;
  redev: Array<{ name: string; href: string; meta: string[]; step: number; total: number; label: string; sortKey: string; thumb?: ThumbData | null }>;
  analysis: Array<{ key: string; title: string; href: string; thumbSrc: string | null; thumbAlt: string | null; meta: MetaItem[]; sortKey: string }>;
  /** region_home1 — 머리줄 「부산 · 10월 18일 기준」(homeScopeLabel). 옛 캐시 응답엔 없을 수 있다(섬은 label 로 조립). */
  scopeLabel?: string;
  /** region_home1 — 히어로 후보(homeHeroCandidates · 지역 일정 임박 상위 5 · 폴백 사슬). region-b 캐러셀 슬라이드. */
  hero?: HeroCard[];
  /** region_home1 — 통계 4칸(지역 · 값 없는 칸 생략). */
  stats?: HeroStat[];
  /**
   * 레인 E — 시·군이 0건인 섹션은 «상위 시·도» 목록으로 채운다(빈 섹션 대신). 키가 있는 섹션의 목록은 그 상위 지역 것이다.
   * 섬은 목록 위에 「{시·군}은 … 없음 · {시·도} 전체에서 보여 드려요」 한 줄을 붙인다. 옛 캐시 응답엔 없다.
   */
  fallback?: Partial<Record<HomeRegionKind, HomeRegionLink>>;
}

/** 레인 E — 상위 지역 대체 안내 한 줄(조사 은/는 · homeRegionEmptyLine 과 같은 규칙). */
export function homeRegionFallbackLine(label: string, kind: HomeRegionKind, parentLabel: string): string {
  return `${homeRegionEmptyLine(label, kind)} · ${String(parentLabel ?? '').trim()} 전체에서 보여 드려요`;
}

/** post1 3 — 홈 분양예정 머리 「전체 N곳 →」 문구(전국·지역 공용). 수를 모르면 「전체 보기 →」. */
export function upcomingMoreLabel(count: number | null | undefined): string {
  return typeof count === 'number' && Number.isFinite(count) && count >= 0 ? `전체 ${Math.floor(count).toLocaleString('ko-KR')}곳 →` : '전체 보기 →';
}

/** post1 3 — 지역 분양예정 목록 목적지. 시·도 1곳이면 공고 전 현장 목록의 그 시·도(/apt/pipeline?region=), 시·군·묶음이면 지역 허브. */
export const PIPELINE_REGION_KEYS: readonly string[] = SIDO_NAMES;
export function upcomingRegionHref(tier: 1 | 2, sidoNames: readonly string[], hubHref: string): string {
  if (tier === 1 && sidoNames.length === 1 && PIPELINE_REGION_KEYS.includes(sidoNames[0])) return `/apt/pipeline?region=${encodeURIComponent(sidoNames[0])}`;
  return hubHref;
}

export type HomeRegionKind = 'schedule' | 'upcoming' | 'redev' | 'analysis';

/** 섹션 0건 문구(섹션 이름 — 빈 상자 금지). */
export const HOME_REGION_NOUN: Record<HomeRegionKind, string> = {
  schedule: '이번 주 청약',
  upcoming: '분양예정 현장',
  redev: '재개발·재건축 현장',
  analysis: '분석 글',
};

/** 가상 페이지뷰 경로(홈 필터 적용 = 같은 경로라 Next pathname effect 가 안 돈다). */
export function homeVirtualPath(code: string): string {
  return `/?r=${encodeURIComponent(code)}`;
}

/** region_home1 규칙 3 — 0건 섹션 한 줄 「부산은 이번 주 청약 없음」(조사 은/는 받침 판정 · 빈 카드 금지). */
export function homeRegionEmptyLine(label: string, kind: HomeRegionKind): string {
  const l = String(label ?? '').trim();
  const last = l.charCodeAt(l.length - 1);
  const josa = last >= 0xac00 && last <= 0xd7a3 ? ((last - 0xac00) % 28 ? '은' : '는') : '은';
  return `${l}${josa} ${HOME_REGION_NOUN[kind]} 없음`;
}
